import { useCallback, useEffect, useRef, useState } from 'react';
import { AssistantError, streamAssistantChat } from '../../api/assistant';
const storageKey = (slug) => `assistant_chat_${slug ?? 'all'}`;
/** Historique gardé pour la session du navigateur, par commune (cahier des charges §9). */
function load(slug) {
    try {
        const raw = sessionStorage.getItem(storageKey(slug));
        if (raw) {
            const parsed = JSON.parse(raw);
            // Une réponse interrompue par un rechargement ne reprendra pas.
            parsed.messages = parsed.messages.map((m) => (m.streaming ? { ...m, streaming: false, error: !m.text, text: m.text || 'Réponse interrompue.' } : m));
            return parsed;
        }
    }
    catch {
        /* stockage indisponible : historique vide */
    }
    return { conversationId: null, messages: [] };
}
let seq = 0;
const localId = () => `m${Date.now()}-${seq++}`;
export function useAssistantChat(slug) {
    const [state, setState] = useState(() => load(slug));
    const abortRef = useRef(null);
    const busy = state.messages.some((m) => m.streaming);
    // Changement de commune (impersonation) : autre conversation.
    useEffect(() => {
        abortRef.current?.abort();
        setState(load(slug));
    }, [slug]);
    useEffect(() => {
        try {
            sessionStorage.setItem(storageKey(slug), JSON.stringify(state));
        }
        catch {
            /* stockage plein ou bloqué : l'historique reste en mémoire */
        }
    }, [slug, state]);
    useEffect(() => () => abortRef.current?.abort(), []);
    const patch = (id, fn) => setState((s) => ({ ...s, messages: s.messages.map((m) => (m.id === id ? fn(m) : m)) }));
    const send = useCallback(async (text, page, simulateOperator) => {
        const question = text.trim();
        if (!question || busy)
            return;
        const answerId = localId();
        setState((s) => ({
            ...s,
            messages: [
                ...s.messages,
                { id: localId(), role: 'user', text: question },
                { id: answerId, role: 'assistant', text: '', streaming: true },
            ],
        }));
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            await streamAssistantChat({
                message: question,
                conversation_id: state.conversationId,
                page,
                ...(simulateOperator ? { simulate_role: 'operator' } : {}),
            }, (e) => {
                switch (e.event) {
                    case 'meta':
                        setState((s) => ({ ...s, conversationId: e.data.conversation_id }));
                        break;
                    case 'sources':
                        patch(answerId, (m) => ({ ...m, sources: e.data.extracts }));
                        break;
                    case 'delta':
                        patch(answerId, (m) => ({ ...m, text: m.text + e.data.text }));
                        break;
                    case 'replace':
                        patch(answerId, (m) => ({ ...m, text: e.data.text, sources: undefined }));
                        break;
                    case 'done':
                        patch(answerId, (m) => ({ ...m, decision: e.data.decision }));
                        break;
                    case 'saved':
                        patch(answerId, (m) => ({ ...m, messageId: e.data.message_id, streaming: false }));
                        break;
                }
            }, controller.signal);
            patch(answerId, (m) => ({ ...m, streaming: false }));
        }
        catch (err) {
            if (controller.signal.aborted)
                return;
            const message = err instanceof AssistantError
                ? err.message
                : "Connexion à l'assistant impossible. Vérifiez votre connexion et réessayez.";
            patch(answerId, (m) => ({ ...m, text: message, error: true, streaming: false }));
        }
    }, [busy, state.conversationId]);
    const reset = useCallback(() => {
        abortRef.current?.abort();
        setState({ conversationId: null, messages: [] });
    }, []);
    const setFeedback = useCallback((id, value) => patch(id, (m) => ({ ...m, feedback: value })), []);
    return { messages: state.messages, busy, send, reset, setFeedback };
}
